import React from 'react';
import { useProfileStore } from '../../../store/profileStore';
import { Profile } from '../../../store/profileStore';
import { KID_SOUNDS, playKidSound } from '../../../utils/kidSounds';
import { THEME_SOUNDS, playThemeSound } from '../../../utils/themeSounds';
import { useThemeStore } from '../../../store/themeStore';
import SoundPicker from '../fields/SoundPicker';

type SubAlarmEditorProps = {
    profile: Profile;
};

const EMOJI_CHOICES = ['🚿', '🪥', '👕', '🎒', '⭐', '🦷', '🛁', '👟', '📚', '🍎', '🥪', '💤'];

const SubAlarmEditor: React.FC<SubAlarmEditorProps> = ({ profile }) => {
    const updateSubAlarm = useProfileStore((state) => state.updateSubAlarm);
    const updateProfile = useProfileStore((state) => state.updateProfile);
    const { selectedTheme } = useThemeStore();

    // Get themed sounds for the currently-selected theme
    const themeId = selectedTheme.id;
    const themedSounds = THEME_SOUNDS[themeId] || [];

    const recurring = profile.recurringWarning;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
                <span className="text-base font-bold">🔔 Sub-Alarms</span>
                <span className="text-xs opacity-60">(total {profile.totalMinutes} min)</span>
            </div>

            {profile.subAlarms.length === 0 && (
                <p className="rounded-lg bg-white/10 p-3 text-sm opacity-70">
                    No sub-alarms yet. The timer will only ring at the end. ⏰
                </p>
            )}

            {/* Recurring warning (Exam mode) */}
            <div
                className="rounded-2xl p-3"
                style={{ backgroundColor: `${selectedTheme.color.point}1a` }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                        <span className="text-sm font-bold">🔄 Recurring Warning</span>
                        <span className="text-xs opacity-60">Plays a sound every N minutes</span>
                    </div>
                    <button
                        onClick={() =>
                            updateProfile(profile.id, {
                                recurringWarning: recurring
                                    ? undefined
                                    : {
                                          intervalMin: 1,
                                          soundSrc: 'kid:lowbeep',
                                          label: '1 minute passed',
                                          emoji: '📝',
                                      },
                            })
                        }
                        className="relative h-8 w-14 rounded-full transition-colors"
                        style={{
                            backgroundColor: recurring
                                ? selectedTheme.color.point
                                : 'rgba(128,128,128,0.4)',
                        }}
                        aria-label="Toggle recurring warning"
                    >
                        <span
                            className={`absolute top-1 size-6 rounded-full bg-white transition-transform ${
                                recurring ? 'translate-x-7' : 'translate-x-1'
                            }`}
                        />
                    </button>
                </div>

                {recurring && (
                    <div className="mt-3 flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <label className="text-xs font-semibold opacity-70">Every</label>
                            <input
                                type="number"
                                min={1}
                                max={60}
                                value={recurring.intervalMin}
                                onChange={(e) =>
                                    updateProfile(profile.id, {
                                        recurringWarning: {
                                            ...recurring,
                                            intervalMin: Number(e.target.value) || 1,
                                        },
                                    })
                                }
                                className="w-14 rounded-lg border px-1 py-1 text-center text-lg font-bold text-black"
                            />
                            <span className="text-xs">min</span>
                            <input
                                type="text"
                                value={recurring.label}
                                onChange={(e) =>
                                    updateProfile(profile.id, {
                                        recurringWarning: { ...recurring, label: e.target.value },
                                    })
                                }
                                className="w-full rounded-lg border px-2 py-1 text-sm text-black"
                                placeholder="Warning label"
                            />
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            {KID_SOUNDS.filter((s) => ['lowbeep', 'ding', 'tick', 'bell'].includes(s.id)).map((s) => {
                                const active = recurring.soundSrc === `kid:${s.id}`;
                                return (
                                    <button
                                        key={s.id}
                                        type="button"
                                        onClick={() => {
                                            updateProfile(profile.id, {
                                                recurringWarning: { ...recurring, soundSrc: `kid:${s.id}` },
                                            });
                                            playKidSound(s.id);
                                        }}
                                        className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition-all ${
                                            active ? 'scale-105 ring-2' : 'hover:scale-105'
                                        }`}
                                        style={{
                                            backgroundColor: active
                                                ? `${selectedTheme.color.point}33`
                                                : 'rgba(255,255,255,0.1)',
                                            boxShadow: active
                                                ? `0 0 0 2px ${selectedTheme.color.point}`
                                                : undefined,
                                        }}
                                    >
                                        <span className="text-lg">{s.emoji}</span>
                                        {s.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Themed sounds banner for the current theme */}
            {themedSounds.length > 0 && (
                <div
                    className="rounded-2xl p-3"
                    style={{ backgroundColor: `${selectedTheme.color.point}1a` }}
                >
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-bold">🎵 {selectedTheme.title} Sounds</span>
                        <span className="text-xs opacity-60">(tap to test)</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {themedSounds.map((s, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => playThemeSound(s.url)}
                                title={s.label}
                                className="flex items-center gap-1 rounded-full px-3 py-1 text-sm font-semibold transition-all hover:scale-105"
                                style={{ backgroundColor: `${selectedTheme.color.point}33` }}
                            >
                                <span className="text-lg">{s.emoji}</span>
                                {s.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {profile.subAlarms.map((sub) => {
                const isKidSound = sub.soundSrc.startsWith('kid:');
                const isThemeSound = sub.soundSrc.startsWith('http');
                const kidSoundId = isKidSound ? sub.soundSrc.slice(4) : '';

                return (
                    <div
                        key={sub.id}
                        className="flex flex-col gap-3 rounded-2xl p-4"
                        style={{ backgroundColor: `${selectedTheme.color.point}1a` }}
                    >
                        {/* Header: emoji + label + time */}
                        <div className="flex items-center gap-3">
                            <select
                                value={sub.emoji}
                                onChange={(e) => updateSubAlarm(profile.id, sub.id, { emoji: e.target.value })}
                                className="rounded-lg border p-1 text-2xl"
                                title="Pick an icon"
                            >
                                {EMOJI_CHOICES.map((em) => (
                                    <option key={em} value={em}>
                                        {em}
                                    </option>
                                ))}
                            </select>
                            <input
                                type="text"
                                value={sub.label}
                                onChange={(e) => updateSubAlarm(profile.id, sub.id, { label: e.target.value })}
                                className="w-full rounded-lg border px-2 py-1 text-sm font-bold text-black"
                                placeholder="What to do?"
                            />
                            <div className="flex shrink-0 items-center gap-1">
                                <input
                                    type="number"
                                    min={0}
                                    max={profile.totalMinutes}
                                    value={sub.atElapsedMin}
                                    onChange={(e) =>
                                        updateSubAlarm(profile.id, sub.id, {
                                            atElapsedMin: Number(e.target.value) || 0,
                                        })
                                    }
                                    className="w-14 rounded-lg border px-1 py-1 text-center text-lg font-bold text-black"
                                />
                                <span className="text-xs">min</span>
                            </div>
                        </div>

                        {/* Current sound indicator */}
                        <div className="text-xs opacity-70">
                            {isThemeSound ? '🔊 Theme sound' : isKidSound ? '🔊 Kid sound' : '🔇 No sound'}
                        </div>

                        {/* Sound picker: themed sounds (if available) */}
                        {themedSounds.length > 0 && (
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-semibold opacity-70">
                                    🎵 {selectedTheme.title} sounds (tap to assign)
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    {themedSounds.map((s, i) => {
                                        const active = isThemeSound && sub.soundSrc === s.url;
                                        return (
                                            <button
                                                key={i}
                                                type="button"
                                                onClick={() => {
                                                    updateSubAlarm(profile.id, sub.id, { soundSrc: s.url });
                                                    playThemeSound(s.url);
                                                }}
                                                className={`flex items-center gap-1 rounded-xl p-2 text-xs transition-all ${
                                                    active ? 'scale-105 ring-2' : 'hover:scale-105'
                                                }`}
                                                style={{
                                                    backgroundColor: active
                                                        ? `${selectedTheme.color.point}33`
                                                        : 'rgba(255,255,255,0.1)',
                                                    boxShadow: active
                                                        ? `0 0 0 2px ${selectedTheme.color.point}`
                                                        : undefined,
                                                }}
                                            >
                                                <span className="text-lg">{s.emoji}</span>
                                                <span className="font-semibold">{s.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Kid sound presets */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-semibold opacity-70">
                                ✨ Kid sounds (tap to assign)
                            </label>
                            <div className="grid grid-cols-4 gap-2">
                                {KID_SOUNDS.map((s) => {
                                    const active = isKidSound && kidSoundId === s.id;
                                    return (
                                        <button
                                            key={s.id}
                                            type="button"
                                            onClick={() => {
                                                updateSubAlarm(profile.id, sub.id, { soundSrc: `kid:${s.id}` });
                                                playKidSound(s.id);
                                            }}
                                            className={`flex flex-col items-center gap-0.5 rounded-xl p-2 text-xs transition-all ${
                                                active ? 'scale-105 ring-2' : 'hover:scale-105'
                                            }`}
                                            style={{
                                                backgroundColor: active
                                                    ? `${selectedTheme.color.point}33`
                                                    : 'rgba(255,255,255,0.1)',
                                                boxShadow: active
                                                    ? `0 0 0 2px ${selectedTheme.color.point}`
                                                    : undefined,
                                            }}
                                        >
                                            <span className="text-2xl">{s.emoji}</span>
                                            <span className="font-semibold">{s.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Advanced: custom URL sound */}
                        <details className="mt-1">
                            <summary className="cursor-pointer text-xs opacity-60">
                                🔧 Advanced: custom sound from web
                            </summary>
                            <div className="mt-2">
                                <SoundPicker
                                    value={isKidSound || isThemeSound ? '' : sub.soundSrc}
                                    onChange={(value) =>
                                        updateSubAlarm(profile.id, sub.id, { soundSrc: value })
                                    }
                                />
                            </div>
                        </details>
                    </div>
                );
            })}
        </div>
    );
};

export default SubAlarmEditor;
